import React from 'react';
import DataManagementVM from '../viewmodel/DataManagementVM';
import RestartAlt from '@mui/icons-material/RestartAlt';
import {
    Box,
    Container,
    Typography,
    Paper,
    Alert,
    Button,
    Grid,
    CircularProgress,
    Autocomplete,
    TextField,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Menu,
    MenuItem,
    FormControl,
    Tooltip,
    Drawer,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Checkbox,
    Tabs,
    Tab,
    FormControlLabel,
    RadioGroup,
    Radio,
    Card,
    CardContent,
    Switch,
    TableSortLabel,
    Badge,
    LinearProgress,
    Stack,
    Collapse,
    InputAdornment,
    FormLabel
} from '@mui/material';
import {
    Storage,
    Add,
    Edit,
    Delete,
    Download,
    Search,
    Refresh,
    Save,
    Cancel,
    MoreVert,
    TableChart,
    ViewColumn,
    DeleteSweep,
    ContentCopy,
    Visibility,
    VisibilityOff,
    PlayArrow,
    Code,
    Clear,
    FilterList,
    FilterListOff,
    Warning,
    Close,
    CloudDownload,
    CloudUpload,
    AddBox,
    Schema,
    CreateNewFolder,
    ExpandMore,
    ExpandLess,
    Remove,
    FirstPage,
    LastPage
} from '@mui/icons-material';
import { useToast } from '../../../basecomp/components/provider/ToastProvider';

const BUTTON_HEIGHT = '32px';
const FONT_SIZE_SMALL = '0.75rem';
const COMMON_COLORS = {
    primary: '#042560',
    primaryHover: '#031e4d',
    secondary: '#3CB043',
    warning: '#f59e0b',
    danger: '#d32f2f',
    success: '#2e7d32',
    purple: '#9c27b0',
    gray: '#6b7280'
};

const createButtonStyles = (color, variant = 'outlined') => ({
    height: BUTTON_HEIGHT,
    fontSize: FONT_SIZE_SMALL,
    textTransform: 'none',
    minWidth: '60px',
    ...(variant === 'contained' ? {
        backgroundColor: color,
        color: 'white',
        '&:hover': { backgroundColor: color === COMMON_COLORS.primary ? COMMON_COLORS.primaryHover : `${color}dd` }
    } : {
        borderColor: color,
        color: color,
        '&:hover': { backgroundColor: `${color}20` }
    })
});

const inputFieldStyles = {
    '& .MuiOutlinedInput-root': {
        height: 56,
        fontSize: '1rem',
        backgroundColor: 'white',
        borderRadius: '3px',
        '& fieldset': {
            borderColor: 'rgba(4, 37, 96, 0.2)',
            borderWidth: '2px'
        },
        '&:hover fieldset': {
            borderColor: COMMON_COLORS.secondary,
            borderWidth: '2px'
        },
        '&.Mui-focused fieldset': {
            borderColor: COMMON_COLORS.primary,
            borderWidth: '2px'
        },
    },
    '& .MuiInputLabel-root': {
        fontSize: '1rem',
        color: COMMON_COLORS.primary,
        fontWeight: 500,
        '&.Mui-focused': {
            color: COMMON_COLORS.primary,
            fontWeight: 600
        },
    },
};

const RibbonSection = ({ label, children }) => (
    <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{
                color: COMMON_COLORS.gray,
                fontSize: '0.8rem',
                fontWeight: 600,
                minWidth: '50px'
            }}>
                {label}
            </Typography>
            {children}
        </Box>
        <Divider orientation="vertical" flexItem sx={{ height: BUTTON_HEIGHT }} />
    </>
);

const FilterControls = ({
    searchQuery,
    handleSearchChange,
    filterStats,
    selectedColumn,
    tableColumns,
    visibleColumns,
    handleFilterChange,
    filterColumn,
    filterValue,
    searchType,
    handleSearchTypeChange,
    availableSearchTypes,
    clearAllFilters,
    exportFilteredData
}) => (
    <RibbonSection label="Filter">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, width: '100%' }}>
            <Badge
                color="primary"
                variant="dot"
                invisible={!searchQuery}
                sx={{ '& .MuiBadge-dot': { backgroundColor: COMMON_COLORS.secondary } }}
            >
                <TextField
                    size="small"
                    placeholder="Search all columns..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ color: COMMON_COLORS.gray, fontSize: '1rem' }} />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={() => handleSearchChange('')}
                                    sx={{ padding: '2px' }}
                                >
                                    <Clear sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        flex: 1,
                        minWidth: 200,
                        maxWidth: 350,
                        '& .MuiOutlinedInput-root': {
                            height: BUTTON_HEIGHT,
                            backgroundColor: searchQuery ? '#f0f9ff' : 'white',
                            borderColor: searchQuery ? COMMON_COLORS.secondary : undefined
                        }
                    }}
                />
            </Badge>

            {searchQuery && (
                <Chip
                    label={`${filterStats.filtered} found`}
                    size="small"
                    color="primary"
                    sx={{
                        height: '20px',
                        fontSize: '0.8rem',
                        backgroundColor: '#e0f2fe',
                        color: COMMON_COLORS.primary
                    }}
                />
            )}

            <Badge
                color="secondary"
                variant="dot"
                invisible={!filterColumn}
                sx={{ '& .MuiBadge-dot': { backgroundColor: COMMON_COLORS.warning } }}
            >
                <Autocomplete
                    size="small"
                    options={tableColumns.filter(col => visibleColumns.includes(col.name))}
                    value={selectedColumn || null}
                    onChange={(event, newValue) => {
                        if (newValue) {
                            handleFilterChange(newValue.name, filterValue);
                            if (newValue.name !== filterColumn) {
                                handleSearchTypeChange('');
                            }
                        } else {
                            handleFilterChange('', '');
                            handleSearchTypeChange('');
                        }
                    }}
                    getOptionLabel={(option) => option.name || ''}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Select column"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: BUTTON_HEIGHT,
                                    backgroundColor: filterColumn ? '#fff7ed' : 'white',
                                    borderColor: filterColumn ? COMMON_COLORS.warning : undefined
                                }
                            }}
                        />
                    )}
                    sx={{ flex: 1, minWidth: 200, maxWidth: 350 }}
                    clearIcon={filterColumn ? <Clear fontSize="small" /> : null}
                />
            </Badge>

            <Autocomplete
                size="small"
                options={availableSearchTypes}
                value={availableSearchTypes.find(type => type.value === searchType) || null}
                onChange={(event, newValue) => handleSearchTypeChange(newValue?.value || '')}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Filter type"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                height: BUTTON_HEIGHT,
                                backgroundColor: searchType ? '#fff7ed' : 'white',
                                borderColor: searchType ? COMMON_COLORS.warning : undefined
                            }
                        }}
                    />
                )}
                sx={{ flex: 1, minWidth: 200, maxWidth: 350 }}
                disabled={!filterColumn}
            />

            <TextField
                size="small"
                placeholder={
                    !filterColumn ? "Select column first" :
                        searchType === 'empty' || searchType === 'not_empty' ? "No value needed" :
                            searchType === 'between' ? "min,max" : "Filter value"
                }
                value={filterValue}
                onChange={(e) => handleFilterChange(filterColumn, e.target.value)}
                disabled={!filterColumn || !searchType || searchType === 'empty' || searchType === 'not_empty'}
                InputProps={{
                    endAdornment: filterValue && (
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                onClick={() => handleFilterChange(filterColumn, '')}
                                sx={{ padding: '2px' }}
                            >
                                <Clear sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                        </InputAdornment>
                    )
                }}
                sx={{
                    flex: 1,
                    minWidth: 200,
                    maxWidth: 350,
                    '& .MuiOutlinedInput-root': {
                        height: BUTTON_HEIGHT,
                        backgroundColor: (filterColumn && searchType) ?
                            (filterValue ? '#fff7ed' : 'white') : '#f9fafb',
                        borderColor: filterValue ? COMMON_COLORS.warning : undefined
                    }
                }}
            />

            <Box sx={{ display: 'flex', gap: 0.5 }}>
                {(filterStats.hasSearch || filterStats.hasColumnFilter) && (
                    <Tooltip title="Clear all filters">
                        <IconButton
                            size="small"
                            onClick={clearAllFilters}
                            sx={{
                                border: `1px solid ${COMMON_COLORS.warning}`,
                                color: COMMON_COLORS.warning,
                                width: BUTTON_HEIGHT,
                                height: BUTTON_HEIGHT,
                                backgroundColor: 'white',
                                '&:hover': { backgroundColor: '#fff7ed' }
                            }}
                        >
                            <FilterListOff sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </Tooltip>
                )}

                {filterStats.isFiltered && (
                    <Tooltip title="Export filtered data">
                        <IconButton
                            size="small"
                            onClick={() => exportFilteredData('csv')}
                            sx={{
                                border: `1px solid ${COMMON_COLORS.success}`,
                                color: COMMON_COLORS.success,
                                width: BUTTON_HEIGHT,
                                height: BUTTON_HEIGHT,
                                backgroundColor: 'white',
                                '&:hover': { backgroundColor: '#e8f5e8' }
                            }}
                        >
                            <Download sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {filterStats.isFiltered && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                    <Chip
                        label={`${filterStats.filtered} of ${filterStats.total} records`}
                        size="small"
                        color="primary"
                        sx={{
                            height: "20px",
                            fontSize: "0.8rem",
                            backgroundColor: "#e0f2fe",
                            color: COMMON_COLORS.primary,
                        }}
                    />
                </Box>
            )}


        </Box>
    </RibbonSection>
);

const TableCellContent = ({
    row,
    column,
    editingRow,
    editingData,
    setEditingData,
    isEditing
}) => {
    const isNewRow = row._isNew;
    const columnKey = column.name;
    const cellValue = row[columnKey];
    const isRequired = !column.nullable && !column.primaryKey;
    const isEmpty = !cellValue || cellValue === '';
    const isPrimaryKey = column.primaryKey;

    if (isEditing) {
        return (
            <TextField
                size="small"
                fullWidth
                value={editingData[columnKey] || ''}
                onChange={(e) =>
                    setEditingData((prev) => ({
                        ...prev,
                        [columnKey]: e.target.value,
                    }))
                }
                disabled={isPrimaryKey}
                error={isRequired && isEmpty}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        height: 32,
                        fontSize: '0.875rem',
                        backgroundColor: isPrimaryKey ? '#f0f9ff' : 'white',
                        color: isPrimaryKey ? COMMON_COLORS.primary : 'inherit',
                        fontWeight: isPrimaryKey ? 600 : 'normal',
                    },
                    '& .MuiFormHelperText-root': {
                        fontSize: '0.8rem',
                        color: '#10b981',
                        marginTop: '2px',
                    },
                }}
                InputProps={isPrimaryKey ? {
                    startAdornment: (
                        <InputAdornment position="start">
                        </InputAdornment>
                    ),
                } : {}}
            />
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {column.dataType === 'boolean' ? (
                <Chip
                    label={cellValue ? 'True' : 'False'}
                    size="small"
                    color={cellValue ? 'success' : 'default'}
                />
            ) : (
                <Typography
                    variant="body2"
                    sx={{
                        color: isEmpty && isRequired ? '#ef4444' : isPrimaryKey ? COMMON_COLORS.primary : 'inherit',
                        fontWeight: isPrimaryKey ? 600 : 'normal',
                    }}
                >
                    {cellValue != null ? cellValue : (isEmpty ? '—' : '')}
                    {isNewRow && isPrimaryKey && cellValue && (
                        <Typography
                            component="span"
                            sx={{
                                ml: 1,
                                color: '#10b981',
                                fontSize: '0.75rem',
                                fontStyle: 'italic',
                            }}
                        >
                            (auto)
                        </Typography>
                    )}
                </Typography>
            )}
        </Box>
    );
};

const DataManagementView = () => {
    const vm = DataManagementVM();
    const { handleToastOpen } = useToast();

    const {
        openTabs,
        databases,
        selectedDatabase,
        tables,
        selectedTable,
        tableData,
        filteredTableData,
        tableColumns,
        dataTypes,
        totalRows,
        isLoading,
        isDatabaseLoading,
        isTableLoading,
        page,
        rowsPerPage,
        searchQuery,
        filterColumn,
        filterValue,
        searchType,
        sortColumn,
        sortDirection,
        editingRow,
        editingData,
        selectedRows,
        visibleColumns,
        showColumnDrawer,
        showQueryDialog,
        customQuery,
        showColumnDialog,
        columnDialogMode,
        columnDialogData,
        showImportDialog,
        importFile,
        importOptions,
        activeTab,
        isAddingNewRow,
        filterStats,
        isExpanded,
        availableSearchTypes,
        headerMenu,
        actionMenu,
        selectedColumn,
        showDeleteDialog,
        showNewTableDialog,
        deleteType,
        sqlTemplateTab,
        hasUserDrop,
        hasUserTruncate,
        addNewColumn,
        removeColumn,
        updateColumn,
        handleExportQueryResults,
        newTableData,
        setNewTableData,
        setEditingData,
        setSelectedRows,
        setVisibleColumns,
        setShowColumnDrawer,
        setShowQueryDialog,
        setCustomQuery,
        setShowColumnDialog,
        setColumnDialogMode,
        setColumnDialogData,
        setShowImportDialog,
        setImportFile,
        setImportOptions,
        setIsExpanded,
        setShowDeleteDialog,
        setShowNewTableDialog,
        setDeleteType,
        setSqlTemplateTab,
        setHeaderMenu,
        setActionMenu,
        handleCloseAllTabs,
        handleDatabaseChange,
        handleTableChange,
        handleRefreshData,
        handleSearchChange,
        handleFilterChange,
        handleSearchTypeChange,
        handleSortChange,
        handlePageChange,
        handleRowsPerPageChange,
        clearAllFilters,
        exportFilteredData,
        handleAddNewRowInline,
        handleEditRow,
        handleSaveEdit,
        handleCancelEdit,
        handleAddColumn,
        handleModifyColumn,
        handleCreateTable,
        handleDeleteColumn,
        handleExecuteQuery,
        handleImportData,
        handleCloseTab,
        handleTabChange,
        getCellStyle,
        showSnackbar,
        handleResetFilters,
        handleUnifiedDelete,
        showExportDialog,
        setShowExportDialog,
        exportFormat,
        setExportFormat,
        handleOpenExportDialog,
        handleConfirmExport,
        hasUserDelete,
        activeQuery,
        showActiveQuery,
        handleCancelActiveQuery,
        handleCancelQueryDialog,
        getSqlTemplates,
        setCreateTableMenu,
        createTableMenu,
        setIsFromSqlCreation
    } = vm;

    const getRowIdentifier = (row, tableColumns) => {
        // Handle new rows first - they should use their temporary ID
        if (row._isNew && row._tempId) {
            return row._tempId;
        }
        
        // Find the primary key column for existing rows
        const primaryKeyColumn = tableColumns.find(col => col.primaryKey);
        
        if (primaryKeyColumn && row[primaryKeyColumn.name] != null) {
            return row[primaryKeyColumn.name];
        }
        
        // Fallback to existing logic if no primary key found
        return row._tempId || row.id || row._id;
    };

    // Get permissions from localStorage
    const permissionData = localStorage.getItem("permissionData") || "";
    const permissions = permissionData.split(",").map(p => p.trim());

    // Check specific permissions
    const hasDevProdAccess = permissions.includes("menu_dev_prod_db");
    const hasDevUatAccess = permissions.includes("menu_dev_uat_db");

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'rgb(254, 254, 254)',
            pt: { xs: '60px', sm: '70px', md: '84px' },
            pb: 4,
            px: { xs: 1, sm: 2, md: 4, lg: 8 }
        }}>
            <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
                <Paper
                    elevation={4}
                    sx={{
                        borderRadius: '16px',
                        overflow: 'hidden',
                        border: '1px solid rgba(60, 176, 67, 0.2)',
                        marginTop: "1.5em",
                        boxShadow: '0 10px 30px rgba(25, 25, 112, 0.1)'
                    }}
                >
                    <Box
                        sx={{
                            background: COMMON_COLORS.secondary,
                            p: { xs: 2, sm: 3, md: 3.5 },
                            color: 'white',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
                                pointerEvents: 'none'
                            }
                        }}
                    >
                        <Grid container spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Storage sx={{ fontSize: 28, color: 'white' }} />
                                    <Typography variant="body1" sx={{
                                        color: 'rgba(255, 255, 255, 0.95)',
                                        fontWeight: 900,
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                                        flexGrow: 1
                                    }}>
                                        Database Management System - Complete pgAdmin-like functionality with CRUD operations, column management, and advanced features
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    {openTabs.length > 0 && (
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1 }}>
                            <Tabs
                                value={activeTab}
                                onChange={(e, newValue) => handleTabChange(newValue)}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{ flexGrow: 1 }}
                            >
                                {openTabs.map((tab, index) => (
                                    <Tab
                                        key={tab.id}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption">{tab.label}</Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCloseTab(index);
                                                    }}
                                                    sx={{ ml: 1, p: 0.5 }}
                                                >
                                                    <Close fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        }
                                    />
                                ))}
                            </Tabs>

                            <Button
                                size="small"
                                onClick={handleCloseAllTabs}
                                sx={{
                                    ml: { xs: 1, sm: 1.5, md: 2 },
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                                    color: COMMON_COLORS.danger,
                                    borderColor: COMMON_COLORS.danger,
                                    border: '1px solid',
                                    height: {
                                        xs: '28px',
                                        sm: '32px',
                                        md: BUTTON_HEIGHT || '36px'
                                    },
                                    minWidth: { xs: '60px', sm: '70px', md: '80px' },
                                    px: { xs: 1, sm: 1.5, md: 2 },
                                    '&:hover': {
                                        backgroundColor: '#ffebee',
                                        borderColor: '#b71c1c',
                                        color: '#b71c1c',
                                    },
                                }}
                                variant="outlined"
                            >
                                <Box component="span" sx={{
                                    display: { xs: 'none', sm: 'inline' }
                                }}>
                                    Close All
                                </Box>
                                <Box component="span" sx={{
                                    display: { xs: 'inline', sm: 'none' }
                                }}>
                                    Close
                                </Box>
                            </Button>
                        </Box>
                    )}

                    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    // options={databases}
                                    options={databases.filter(db => {
                                        // Always exclude these databases
                                        if (db === "postgres" || db === "rdsadmin") {
                                            return false;
                                        }

                                        // Handle conas-prod-db specifically
                                        if (db === "conas-prod-db") {
                                            return hasDevProdAccess;
                                        }

                                        return hasDevUatAccess;
                                    })}
                                    getOptionLabel={(option) => String(option)}
                                    value={databases.find(db => db === selectedDatabase) || null}
                                    onChange={(event, newValue) => handleDatabaseChange(newValue)}
                                    loading={isDatabaseLoading}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Database"
                                            placeholder="Choose a database"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {isDatabaseLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                            sx={inputFieldStyles}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={tables.filter(table => table !== 'none')}
                                    getOptionLabel={(option) => String(option)}
                                    value={tables.find(table => table === selectedTable) || null}
                                    onChange={(event, newValue) => handleTableChange(newValue)}
                                    disabled={!selectedDatabase}
                                    loading={isTableLoading}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={selectedDatabase ? "Select Table" : "Select Database First"}
                                            placeholder={selectedDatabase ? "Choose a table" : ""}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {isTableLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                            sx={inputFieldStyles}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        {selectedTable && !showActiveQuery && (

                            <Card sx={{ mb: 3, border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                                <CardContent sx={{ p: 0 }}>
                                    <Box sx={{
                                        p: 2,
                                        backgroundColor: '#f8fafc',
                                        borderBottom: '1px solid #e2e8f0',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'pointer'
                                    }} onClick={() => setIsExpanded(!isExpanded)}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <TableChart sx={{ color: COMMON_COLORS.primary }} />
                                            <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 600 }}>
                                                Database Controls
                                            </Typography>
                                            <Chip
                                                label={`${selectedDatabase}.${selectedTable}`}
                                                size="small"
                                                sx={{ backgroundColor: '#e0f2fe', color: COMMON_COLORS.primary, fontSize: '0.85rem' }}
                                            />
                                        </Stack>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="caption" sx={{ color: COMMON_COLORS.gray }}>
                                                {totalRows || 0} records
                                            </Typography>
                                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                        </Box>
                                    </Box>

                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                        <Box sx={{
                                            backgroundColor: '#f8fafc',
                                            borderBottom: '1px solid #e2e8f0',
                                            p: 1.5
                                        }}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 2,
                                                flexWrap: 'wrap',
                                                minHeight: '48px',
                                                '& > *': {
                                                    marginBottom: '12px'
                                                }
                                            }}>

                                                <FilterControls
                                                    searchQuery={searchQuery}
                                                    handleSearchChange={handleSearchChange}
                                                    filterStats={filterStats}
                                                    selectedColumn={selectedColumn}
                                                    tableColumns={tableColumns}
                                                    visibleColumns={visibleColumns}
                                                    handleFilterChange={handleFilterChange}
                                                    filterColumn={filterColumn}
                                                    filterValue={filterValue}
                                                    searchType={searchType}
                                                    handleSearchTypeChange={handleSearchTypeChange}
                                                    availableSearchTypes={availableSearchTypes}
                                                    clearAllFilters={clearAllFilters}
                                                    exportFilteredData={exportFilteredData}
                                                />

                                                <RibbonSection label="Records">
                                                    <Tooltip title="Add new row in the table">
                                                        <Box style={{ marginLeft: "1em" }}>
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                startIcon={<AddBox />}
                                                                onClick={handleAddNewRowInline}
                                                                disabled={isAddingNewRow}
                                                                sx={createButtonStyles('#1976d2', 'contained')}
                                                            >
                                                                Add New Row
                                                            </Button>
                                                        </Box>
                                                    </Tooltip>
                                                    {hasUserDelete && (
                                                        <Tooltip title="Delete rows from the table">
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                startIcon={<DeleteSweep />}
                                                                onClick={() => {
                                                                    setDeleteType('selected');  // ← Add this line
                                                                    setShowDeleteDialog(true);
                                                                }}
                                                                disabled={selectedRows.length === 0}
                                                                sx={{
                                                                    ...createButtonStyles(selectedRows.length > 0 ? COMMON_COLORS.danger : '#9e9e9e'),
                                                                    '&:hover': {
                                                                        backgroundColor: selectedRows.length > 0 ? '#ffebee' : '#f5f5f5'
                                                                    }
                                                                }}
                                                            >
                                                                Delete ({selectedRows.length})
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                </RibbonSection>

                                                <RibbonSection label="View">
                                                    <Tooltip title="Refresh table">
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleRefreshData}
                                                            sx={{
                                                                border: '1px solid #e0e0e0',
                                                                width: '33px',
                                                                height: '33px',
                                                                backgroundColor: 'white',
                                                                '&:hover': { backgroundColor: '#f5f5f5' }
                                                            }}
                                                        >
                                                            <Refresh sx={{ fontSize: '1.3rem' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Hide columns in the table">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setShowColumnDrawer(true)}
                                                            sx={{
                                                                border: '1px solid #e0e0e0',
                                                                width: '33px',
                                                                height: '33px',
                                                                backgroundColor: 'white',
                                                                '&:hover': { backgroundColor: '#f5f5f5' }
                                                            }}
                                                        >
                                                            <ViewColumn sx={{ fontSize: '1.rem' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {visibleColumns.length < tableColumns.length && (
                                                        <Tooltip title="Export visible columns only">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => exportFilteredData('csv')}
                                                                sx={{
                                                                    border: `1px solid ${COMMON_COLORS.success}`,
                                                                    color: COMMON_COLORS.success,
                                                                    width: BUTTON_HEIGHT,
                                                                    height: BUTTON_HEIGHT,
                                                                    backgroundColor: 'white',
                                                                    '&:hover': { backgroundColor: '#e8f5e8' }
                                                                }}
                                                            >
                                                                <Download sx={{ fontSize: '1rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </RibbonSection>

                                                <RibbonSection label="Data">
                                                    <Tooltip title="Import xlsx/csv files">
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<CloudUpload />}
                                                            onClick={() => setShowImportDialog(true)}
                                                            sx={createButtonStyles(COMMON_COLORS.success)}
                                                        >
                                                            Import
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip title="Export xlsx/csv files">
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<CloudDownload />}
                                                            onClick={handleOpenExportDialog}
                                                            sx={createButtonStyles(COMMON_COLORS.success)}
                                                        >
                                                            Export
                                                        </Button>
                                                    </Tooltip>
                                                </RibbonSection>

                                                <RibbonSection label="Query">
                                                    <Tooltip title="Code editor">
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={<Code />}
                                                            onClick={() => setShowQueryDialog(true)}
                                                            sx={createButtonStyles(COMMON_COLORS.purple, 'contained')}
                                                        >
                                                            SQL
                                                        </Button>
                                                    </Tooltip>
                                                </RibbonSection>

                                                <RibbonSection label="Table">
                                                    <Tooltip title="Add column in the table">
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<Schema />}
                                                            onClick={() => {
                                                                setColumnDialogMode('add');
                                                                setShowColumnDialog(true);
                                                            }}
                                                            sx={createButtonStyles('#36091B')}
                                                        >
                                                            Add Column
                                                        </Button>
                                                    </Tooltip>

                                                    <Tooltip title="Create new table">
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<CreateNewFolder />}
                                                            onClick={() => setShowNewTableDialog(true)}
                                                            sx={createButtonStyles('#1976d2')}
                                                        >
                                                            Add Table
                                                        </Button>
                                                    </Tooltip>
                                                </RibbonSection>

                                                <RibbonSection label="Operations">
                                                    <Box style={{ marginLeft: "1em" }}>
                                                        {hasUserTruncate && (
                                                            <Tooltip title="Truncate table">
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    startIcon={<DeleteSweep />}
                                                                    onClick={() => {
                                                                        setDeleteType('truncate');
                                                                        setShowDeleteDialog(true);
                                                                    }}
                                                                    sx={createButtonStyles('#fb8c00')}
                                                                >
                                                                    Truncate
                                                                </Button>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                    {hasUserDrop && (
                                                        <Tooltip title="Delete table">
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                startIcon={<Delete />}
                                                                onClick={() => {
                                                                    setDeleteType('drop');
                                                                    setShowDeleteDialog(true);
                                                                }}
                                                                sx={createButtonStyles(COMMON_COLORS.danger)}
                                                            >
                                                                Drop Table
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Reset All">
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<RestartAlt />}
                                                            onClick={handleResetFilters}
                                                            sx={createButtonStyles('#1976d2')}
                                                        >
                                                            Reset
                                                        </Button>
                                                    </Tooltip>
                                                </RibbonSection>
                                            </Box>

                                            {(filterStats.hasSearch || filterStats.hasColumnFilter) && (
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1, px: 1 }}>
                                                    {filterStats.hasSearch && (
                                                        <Chip
                                                            label={`Search: "${searchQuery}"`}
                                                            size="small"
                                                            onDelete={() => handleSearchChange('')}
                                                            deleteIcon={<Clear />}
                                                            sx={{
                                                                height: '20px',
                                                                fontSize: '0.65rem',
                                                                backgroundColor: '#e0f2fe',
                                                                color: COMMON_COLORS.primary
                                                            }}
                                                        />
                                                    )}
                                                    {filterStats.hasColumnFilter && (
                                                        <Chip
                                                            label={`${filterColumn}: ${searchType} "${filterValue}"`}
                                                            size="small"
                                                            onDelete={() => {
                                                                handleFilterChange('', '');
                                                                handleSearchTypeChange('');
                                                            }}
                                                            deleteIcon={<Clear />}
                                                            sx={{
                                                                height: '20px',
                                                                fontSize: '0.65rem',
                                                                backgroundColor: '#fff7ed',
                                                                color: COMMON_COLORS.warning
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </Collapse>
                                </CardContent>
                            </Card>
                        )}

                        {showActiveQuery && activeQuery && (
                            <Card sx={{
                                mb: 3,
                                border: '1px solid #3CB043',
                                borderRadius: '12px',
                                backgroundColor: '#f0fdf4'
                            }}>
                                <CardContent sx={{ p: 0 }}>
                                    <Box sx={{
                                        p: 2,
                                        backgroundColor: '#dcfce7',
                                        borderBottom: '1px solid #bbf7d0',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Code sx={{ color: '#16a34a', fontSize: 20 }} />
                                            <Typography variant="body2" sx={{
                                                color: '#16a34a',
                                                fontWeight: 600,
                                                fontSize: '0.875rem'
                                            }}>
                                                Query Results
                                            </Typography>
                                            <Chip
                                                label={`${filteredTableData.length} rows`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: '#bbf7d0',
                                                    color: '#16a34a',
                                                    fontWeight: 500,
                                                    fontSize: '0.75rem'
                                                }}
                                            />
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <Tooltip title="Close query results">
                                                <IconButton
                                                    size="small"
                                                    onClick={handleCancelActiveQuery}
                                                    sx={{
                                                        border: '1px solid #d1fae5',
                                                        color: '#16a34a',
                                                        backgroundColor: 'white',
                                                        width: 32,
                                                        height: 32,
                                                        '&:hover': {
                                                            backgroundColor: '#f0fdf4',
                                                            borderColor: '#16a34a',
                                                            color: '#15803d'
                                                        }
                                                    }}
                                                >
                                                    <Close sx={{ fontSize: '1.5rem' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    <Box sx={{ p: 2, backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
                                        <Box sx={{
                                            position: 'relative',
                                            backgroundColor: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '6px',
                                            overflow: 'hidden'
                                        }}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                value={customQuery}
                                                onChange={(e) => setCustomQuery(e.target.value)}
                                                variant="outlined"
                                                placeholder="Your executed query will appear here..."
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                                        fontSize: '0.9rem',
                                                        lineHeight: 1.4,
                                                        backgroundColor: '#f8fafc',
                                                        color: '#374151',
                                                        border: 'none',
                                                        '& fieldset': { border: 'none' },
                                                        '&:hover fieldset': { border: 'none' },
                                                        '&.Mui-focused fieldset': { border: 'none' },
                                                        '& textarea': {
                                                            maxHeight: '150px',
                                                            overflow: 'auto !important',
                                                            resize: 'vertical'
                                                        }
                                                    }
                                                }}
                                                rows={10}
                                                maxRows={10}
                                            />

                                            <Tooltip title="Copy query to clipboard">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(activeQuery);
                                                        showSnackbar('Query copied to clipboard', 'success');
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        backgroundColor: 'white',
                                                        border: '1px solid #e2e8f0',
                                                        color: '#6b7280',
                                                        width: 24,
                                                        height: 24,
                                                        '&:hover': {
                                                            backgroundColor: '#f9fafb',
                                                            borderColor: '#16a34a',
                                                            color: '#16a34a'
                                                        }
                                                    }}
                                                >
                                                    <ContentCopy sx={{ fontSize: '1.2rem' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    <Box sx={{
                                        p: 1.5,
                                        backgroundColor: '#f9fafb',
                                        display: 'flex',
                                        gap: 1,
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap'
                                    }}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<PlayArrow />}
                                                onClick={handleExecuteQuery}
                                                disabled={!activeQuery.trim()}
                                                sx={{
                                                    backgroundColor: '#16a34a',
                                                    color: 'white',
                                                    height: '35px',
                                                    fontSize: '0.8rem',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: '#15803d'
                                                    },
                                                    '&:disabled': {
                                                        backgroundColor: '#e5e7eb',
                                                        color: '#9ca3af'
                                                    }
                                                }}
                                            >
                                                Re-execute
                                            </Button>

                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<Download />}
                                                onClick={handleExportQueryResults}
                                                disabled={filteredTableData.length === 0}
                                                sx={{
                                                    borderColor: COMMON_COLORS.success,
                                                    color: COMMON_COLORS.success,
                                                    height: '35px',
                                                    fontSize: '0.8rem',
                                                    textTransform: 'none',
                                                    '&:hover': { backgroundColor: '#e8f5e8' }
                                                }}
                                            >
                                                Export Results
                                            </Button>
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <Chip
                                                label={`${filteredTableData.length} rows returned`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: '#e0f2fe',
                                                    color: '#042560',
                                                    fontSize: '0.7rem',
                                                    height: '20px'
                                                }}
                                            />
                                            <Chip
                                                label={`${visibleColumns.length} columns`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: '#fff7ed',
                                                    color: '#ea580c',
                                                    fontSize: '0.7rem',
                                                    height: '20px'
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    {filteredTableData.length === 0 ? (
                                        <Box sx={{
                                            p: 2,
                                            textAlign: 'center',
                                            backgroundColor: 'white',
                                            borderTop: '1px solid #e5e7eb'
                                        }}>
                                            <Typography variant="body2" sx={{
                                                color: '#6b7280',
                                                fontStyle: 'italic',
                                                fontSize: '0.8rem'
                                            }}>
                                                Query executed successfully but returned no results.
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <></>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {isLoading && (
                            <Box sx={{ mb: 3 }}>
                                <LinearProgress sx={{ borderRadius: '4px', height: '6px' }} />
                                <Typography variant="body2" sx={{
                                    mt: 1,
                                    color: '#374151',
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    textAlign: 'center'
                                }}>
                                    Loading table data...
                                </Typography>
                            </Box>
                        )}

                        {selectedTable && tableData.length > 0 && !isLoading && (
                            <Paper
                                sx={{
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                }}
                            >
                                <TableContainer sx={{ maxHeight: 600 }}>
                                    <Table
                                        stickyHeader
                                        size="small"
                                        sx={{
                                            borderLeft: '1px solid #cbd5e1',
                                            borderRight: '1px solid #cbd5e1',
                                            '& th': {
                                                borderRight: '1px solid #e2e8f0',
                                                borderBottom: '2px solid #cbd5e1',
                                                backgroundColor: '#f8fafc',
                                                fontWeight: 600,
                                            },
                                            '& th:last-child': {
                                                borderRight: 'none',
                                            },
                                            '& td': {
                                                borderRight: '1px solid #e2e8f0',
                                                borderBottom: '1px solid #e2e8f0',
                                            },
                                            '& td:last-child': {
                                                borderRight: 'none',
                                            },
                                        }}
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell
                                                    padding="checkbox"
                                                    sx={{
                                                        backgroundColor: '#f8fafc',
                                                        borderBottom: '2px solid #cbd5e1',
                                                        width: 48,
                                                    }}
                                                >
                                                    <Checkbox
                                                        size="small"
                                                        onChange={(e) => {
                                                            if (selectedRows.length > 0) {
                                                                setSelectedRows([]);
                                                            } else {
                                                                const allIds = tableData
                                                                    .filter((row) => !row._isNew)
                                                                    .map((row) => getRowIdentifier(row, tableColumns)) // Use dynamic identifier
                                                                    .filter(id => id != null); // Filter out null/undefined IDs
                                                                setSelectedRows(allIds);
                                                            }
                                                        }}
                                                        checked={
                                                            selectedRows.length > 0 &&
                                                            selectedRows.length === tableData.filter((row) => !row._isNew).length
                                                        }
                                                        indeterminate={
                                                            selectedRows.length > 0 &&
                                                            selectedRows.length < tableData.filter((row) => !row._isNew).length
                                                        }
                                                        title={
                                                            selectedRows.length > 0
                                                                ? `Uncheck all (${selectedRows.length} selected)`
                                                                : "Select all"
                                                        }
                                                    />
                                                </TableCell>
                                                {tableColumns
                                                    .filter((col) => visibleColumns.includes(col.name))
                                                    .map((column) => (
                                                        <TableCell key={column.name}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <TableSortLabel
                                                                    active={sortColumn === column.name}
                                                                    direction={
                                                                        sortColumn === column.name
                                                                            ? sortDirection.toLowerCase()
                                                                            : 'asc'
                                                                    }
                                                                    onClick={() => handleSortChange(column.name)}
                                                                    sx={{ flex: 1 }}
                                                                >
                                                                    <Box>
                                                                        <Typography
                                                                            variant="subtitle2"
                                                                            sx={{
                                                                                fontWeight: 600,
                                                                                lineHeight: 1.2,
                                                                                color: '#111827',
                                                                                userSelect: 'none',
                                                                            }}
                                                                        >
                                                                            {column.name}
                                                                        </Typography>
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 0.5,
                                                                                minWidth: 0,
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    color: '#64748b',
                                                                                    lineHeight: 1.2,
                                                                                    whiteSpace: 'nowrap',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    maxWidth: '100%',
                                                                                    userSelect: 'none',
                                                                                }}
                                                                                title={column.dataType}
                                                                            >
                                                                                {column.dataType}
                                                                            </Typography>
                                                                            {!column.nullable && !column.primaryKey && (
                                                                                <Box
                                                                                    component="span"
                                                                                    sx={{
                                                                                        width: 6,
                                                                                        height: 6,
                                                                                        borderRadius: '50%',
                                                                                        bgcolor: '#ef4444',
                                                                                        display: 'inline-block',
                                                                                        userSelect: 'none',
                                                                                    }}
                                                                                    title="Required"
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                </TableSortLabel>

                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => setHeaderMenu({ anchor: e.currentTarget, column })}
                                                                    sx={{ userSelect: 'none' }}
                                                                >
                                                                    <MoreVert fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        </TableCell>
                                                    ))}

                                                <TableCell
                                                    sx={{
                                                        backgroundColor: '#f8fafc',
                                                        borderBottom: '2px solid #cbd5e1',
                                                        width: 100,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    Actions
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {tableData.map((row, index) => {
                                                const rowId = getRowIdentifier(row, tableColumns); // Use dynamic identifier instead of hardcoded logic
                                                const isNewRow = row._isNew;
                                                const isEditing = editingRow === rowId;

                                                return (
                                                    <TableRow
                                                        key={rowId || `row-${index}`}
                                                        sx={{
                                                            '&:hover': { backgroundColor: '#f9fafb' },
                                                            backgroundColor:
                                                                isEditing
                                                                    ? '#eff6ff'
                                                                    : isNewRow
                                                                        ? '#f0f9ff'
                                                                        : 'inherit',
                                                        }}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            {!isNewRow ? (
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={selectedRows.includes(getRowIdentifier(row, tableColumns))} // Use dynamic identifier
                                                                    onChange={(e) => {
                                                                        const rowId = getRowIdentifier(row, tableColumns); // Use dynamic identifier
                                                                        if (e.target.checked) {
                                                                            setSelectedRows((prev) => [...prev, rowId]);
                                                                        } else {
                                                                            setSelectedRows((prev) =>
                                                                                prev.filter((id) => id !== rowId)
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Chip label="NEW" size="small" color="primary" />
                                                            )}
                                                        </TableCell>

                                                        {tableColumns
                                                            .filter((col) => visibleColumns.includes(col.name))
                                                            .map((column) => (
                                                                <TableCell key={column.name} sx={getCellStyle(column, row[column.name], row)}>
                                                                    <TableCellContent
                                                                        row={row}
                                                                        column={column}
                                                                        editingRow={editingRow}
                                                                        editingData={editingData}
                                                                        setEditingData={setEditingData}
                                                                        getCellStyle={getCellStyle}
                                                                        rowId={rowId}
                                                                        isEditing={isEditing}
                                                                    />
                                                                </TableCell>
                                                            ))}

                                                        <TableCell>
                                                            {isEditing ? (
                                                                <Stack direction="row" spacing={0.5}>
                                                                    <IconButton size="small" onClick={handleSaveEdit} sx={{ color: COMMON_COLORS.primary }}>
                                                                        <Save fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton size="small" onClick={handleCancelEdit} sx={{ color: COMMON_COLORS.gray }}>
                                                                        <Cancel fontSize="small" />
                                                                    </IconButton>
                                                                </Stack>
                                                            ) : (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => setActionMenu({
                                                                        anchor: e.currentTarget,
                                                                        rowId: getRowIdentifier(row, tableColumns) // Use dynamic identifier
                                                                    })}
                                                                    sx={{ color: COMMON_COLORS.gray }}
                                                                >
                                                                    <MoreVert fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>


                                <TablePagination
                                    component="div"
                                    count={totalRows}
                                    page={page}
                                    onPageChange={(e, newPage) => handlePageChange(newPage)}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={(e) => handleRowsPerPageChange(parseInt(e.target.value, 10))}
                                    rowsPerPageOptions={[25, 50, 100, 200, 300, 400, 500]}
                                    showFirstButton={true}
                                    showLastButton={true}
                                    sx={{
                                        borderTop: '1px solid #e2e8f0',
                                        backgroundColor: '#f8fafc',
                                    }}
                                />
                            </Paper>
                        )}

                        {!selectedDatabase && (
                            <Box sx={{
                                textAlign: 'center',
                                py: 6,
                                backgroundColor: 'rgba(248, 250, 252, 0.5)',
                                borderRadius: '3px',
                                border: '2px dashed rgba(60, 176, 67, 0.3)'
                            }}>
                                <Storage sx={{ fontSize: 48, color: COMMON_COLORS.secondary, mb: 2 }} />
                                <Typography variant="h6" sx={{
                                    fontWeight: 600,
                                    color: COMMON_COLORS.primary,
                                    mb: 1,
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                }}>
                                    Select a Database
                                </Typography>
                                <Typography variant="body2" sx={{
                                    color: COMMON_COLORS.gray,
                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}>
                                    Choose a database to start managing your data tables.
                                </Typography>
                            </Box>
                        )}

                        {selectedDatabase && !selectedTable && (
                            <Box sx={{
                                textAlign: 'center',
                                py: 6,
                                backgroundColor: 'rgba(248, 250, 252, 0.5)',
                                borderRadius: '3px',
                                border: '2px dashed rgba(60, 176, 67, 0.3)'
                            }}>
                                <TableChart sx={{ fontSize: 48, color: COMMON_COLORS.secondary, mb: 2 }} />

                                {tables.includes('none') ? (
                                    <>
                                        <Typography variant="h6" sx={{
                                            fontWeight: 600,
                                            color: COMMON_COLORS.primary,
                                            mb: 1,
                                            fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                        }}>
                                            No Tables Found
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: COMMON_COLORS.gray,
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            mb: 3
                                        }}>
                                            This database doesn't have any tables yet. Create your first table to get started.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            startIcon={<CreateNewFolder />}
                                            onClick={() => setCreateTableMenu(true)}
                                            sx={{
                                                backgroundColor: COMMON_COLORS.primary,
                                                color: 'white',
                                                '&:hover': { backgroundColor: COMMON_COLORS.primaryHover },
                                                fontSize: '1rem',
                                                py: 1.5,
                                                px: 3
                                            }}
                                        >
                                            Create First Table
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="h6" sx={{
                                            fontWeight: 600,
                                            color: COMMON_COLORS.primary,
                                            mb: 1,
                                            fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                        }}>
                                            Select a Table
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: COMMON_COLORS.gray,
                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                        }}>
                                            Choose a table from the selected database to view and edit data.
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        )}

                        <Dialog
                            open={createTableMenu}
                            onClose={() => setCreateTableMenu(false)}
                            maxWidth="sm"
                            fullWidth
                            PaperProps={{
                                sx: {
                                    borderRadius: '12px',
                                    overflow: 'hidden'
                                }
                            }}
                        >
                            <DialogTitle sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                backgroundColor: COMMON_COLORS.secondary,
                                color: 'white',
                                py: 2
                            }}>
                                <CreateNewFolder sx={{ color: 'white' }} />
                                Choose Table Creation Method
                            </DialogTitle>

                            <DialogContent sx={{ p: 0 }}>
                                <Box sx={{ p: 3 }}>
                                    <Typography variant="body1" sx={{
                                        mb: 3,
                                        color: '#374151',
                                        textAlign: 'center'
                                    }}>
                                        How would you like to create your table?
                                    </Typography>

                                    <Grid container spacing={2}>
                                        {/* Manual Creation Option */}
                                        <Grid item xs={12} sm={6}>
                                            <Paper
                                                onClick={() => {
                                                    setCreateTableMenu(false);
                                                    setShowNewTableDialog(true);
                                                }}
                                                sx={{
                                                    p: 3,
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    border: '2px solid transparent',
                                                    borderRadius: '12px',
                                                    backgroundColor: '#f8fafc',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        borderColor: COMMON_COLORS.primary,
                                                        backgroundColor: '#f0f9ff',
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 4px 12px rgba(4, 37, 96, 0.15)'
                                                    }
                                                }}
                                            >
                                                <Box sx={{
                                                    width: 64,
                                                    height: 64,
                                                    backgroundColor: COMMON_COLORS.primary,
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mx: 'auto',
                                                    mb: 2
                                                }}>
                                                    <Schema sx={{ fontSize: 32, color: 'white' }} />
                                                </Box>
                                                <Typography variant="h6" sx={{
                                                    fontWeight: 600,
                                                    color: COMMON_COLORS.primary,
                                                    mb: 1
                                                }}>
                                                    Manual Creation
                                                </Typography>
                                                <Typography variant="body2" sx={{
                                                    color: '#6b7280',
                                                    lineHeight: 1.5
                                                }}>
                                                    Use our visual interface to design your table with columns, data types, and constraints
                                                </Typography>
                                                <Box sx={{ mt: 2 }}>
                                                    <Chip
                                                        label="Beginner Friendly"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#dcfce7',
                                                            color: '#16a34a',
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                </Box>
                                            </Paper>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Paper
                                                onClick={() => {
                                                    setCreateTableMenu(false);
                                                    setShowQueryDialog(true);
                                                    setSqlTemplateTab(hasUserDelete ? 4 : 3);
                                                    setIsFromSqlCreation(true);
                                                }}
                                                sx={{
                                                    p: 3,
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    border: '2px solid transparent',
                                                    borderRadius: '12px',
                                                    backgroundColor: '#f8fafc',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        borderColor: COMMON_COLORS.purple,
                                                        backgroundColor: '#faf5ff',
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 4px 12px rgba(156, 39, 176, 0.15)'
                                                    }
                                                }}
                                            >
                                                <Box sx={{
                                                    width: 64,
                                                    height: 64,
                                                    backgroundColor: COMMON_COLORS.purple,
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mx: 'auto',
                                                    mb: 2
                                                }}>
                                                    <Code sx={{ fontSize: 32, color: 'white' }} />
                                                </Box>
                                                <Typography variant="h6" sx={{
                                                    fontWeight: 600,
                                                    color: COMMON_COLORS.purple,
                                                    mb: 1
                                                }}>
                                                    SQL Creation
                                                </Typography>
                                                <Typography variant="body2" sx={{
                                                    color: '#6b7280',
                                                    lineHeight: 1.5
                                                }}>
                                                    Write your own CREATE TABLE statement with full control over all database features
                                                </Typography>
                                                <Box sx={{ mt: 2 }}>
                                                    <Chip
                                                        label="Advanced"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#fef3c7',
                                                            color: '#d97706',
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    <Box sx={{
                                        mt: 3,
                                        p: 2,
                                        backgroundColor: '#f0f9ff',
                                        borderRadius: '8px',
                                        border: '1px solid #dbeafe'
                                    }}>
                                        <Typography variant="body2" sx={{
                                            color: '#1e40af',
                                            textAlign: 'center',
                                            fontWeight: 500
                                        }}>
                                            💡 Tip: You can always switch between methods later. Manual creation is great for beginners, while SQL gives you complete control.
                                        </Typography>
                                    </Box>
                                </Box>
                            </DialogContent>

                            <DialogActions sx={{ p: 2, backgroundColor: '#f8fafc' }}>
                                <Button
                                    onClick={() => setCreateTableMenu(false)}
                                    variant="outlined"
                                    sx={{
                                        borderColor: '#d1d5db',
                                        color: '#6b7280',
                                        '&:hover': {
                                            backgroundColor: '#f9fafb',
                                            borderColor: '#9ca3af'
                                        }
                                    }}
                                >
                                    Cancel
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {selectedTable && tableData.length === 0 && !isLoading && (
                            <Box sx={{
                                textAlign: 'center',
                                py: 6,
                                backgroundColor: 'rgba(248, 250, 252, 0.5)',
                                borderRadius: '3px',
                                border: '2px dashed rgba(156, 163, 175, 0.3)'
                            }}>
                                <TableChart sx={{ fontSize: 48, color: '#d1d5db', mb: 2 }} />
                                <Typography variant="h6" sx={{
                                    fontWeight: 600,
                                    color: '#374151',
                                    mb: 1,
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                }}>
                                    No Data Available
                                </Typography>
                                <Typography variant="body2" sx={{
                                    color: COMMON_COLORS.gray,
                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}>
                                    This table is empty. Add some data to get started.
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<Add />}
                                    onClick={handleAddNewRowInline}
                                    sx={{
                                        mt: 2,
                                        backgroundColor: COMMON_COLORS.primary,
                                        color: 'white',
                                        '&:hover': { backgroundColor: COMMON_COLORS.primaryHover },
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    Add First Row
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Paper>

                <Drawer
                    anchor="right"
                    open={showColumnDrawer}
                    onClose={() => setShowColumnDrawer(false)}
                >
                    <Box sx={{ width: 320, p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Column Visibility
                        </Typography>
                        <List>
                            {tableColumns.map((column) => (
                                <ListItem key={column.name} dense>
                                    <ListItemIcon>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                if (visibleColumns.includes(column.name)) {
                                                    setVisibleColumns(prev => prev.filter(name => name !== column.name));
                                                } else {
                                                    setVisibleColumns(prev => [...prev, column.name]);
                                                }
                                            }}
                                        >
                                            {visibleColumns.includes(column.name) ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                                                    {column.name}
                                                </Typography>
                                                {column.primaryKey && <Chip label="PK" size="small" color="primary" />}
                                                {!column.nullable && <Chip label="!" size="small" color="error" />}
                                            </Box>
                                        }
                                        secondary={
                                            <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                                {column.formattedType || column.dataType}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Drawer>

                <Dialog
                    open={showNewTableDialog}
                    onClose={() => setShowNewTableDialog(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        backgroundColor: '#3CB043',
                        borderBottom: '1px solid #e2e8f0',
                        color: "#ffffff"
                    }}>
                        <CreateNewFolder sx={{ color: '#ffffff' }} />
                        Create New Table
                    </DialogTitle>

                    <DialogContent sx={{ p: 0 }}>
                        <Box sx={{ p: 3, borderBottom: newTableData.tableName.trim() ? '1px solid #e2e8f0' : 'none', backgroundColor: '#ffffff' }}>
                            <TextField
                                fullWidth
                                label="Table Name"
                                size="small"
                                placeholder="Enter table name..."
                                value={newTableData.tableName}
                                onChange={(e) => setNewTableData(prev => ({ ...prev, tableName: e.target.value }))}
                                sx={{
                                    maxWidth: 400,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'white'
                                    }
                                }}
                            />
                            {!newTableData.tableName.trim() && (
                                <Typography variant="body2" sx={{ mt: 1, color: '#6b7280', fontStyle: 'italic' }}>
                                    Enter a table name to configure columns
                                </Typography>
                            )}
                        </Box>

                        {newTableData.tableName.trim() && (
                            <Box sx={{ backgroundColor: '#f8fafc' }}>
                                {/* Header */}
                                <Box sx={{
                                    p: 2,
                                    borderBottom: '1px solid #e2e8f0',
                                    backgroundColor: '#ffffff'
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                                            Column Configuration
                                        </Typography>
                                        <Chip
                                            label={`${newTableData.columns.length} columns`}
                                            size="small"
                                            sx={{ backgroundColor: '#e0f2fe', color: '#042560' }}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    fontWeight: 600,
                                    p: 2,
                                    gap: 2,
                                    alignItems: 'center',
                                    backgroundColor: '#f1f5f9',
                                    borderBottom: '1px solid #e2e8f0',
                                    color: '#475569'
                                }}>
                                    <Box sx={{ flex: 2.5, minWidth: 0 }}>Column Name</Box>
                                    <Box sx={{ flex: 2.5, minWidth: 0 }}>Data Type</Box>
                                    <Box sx={{ flex: 1, textAlign: 'center', minWidth: 80 }}>Primary Key</Box>
                                    <Box sx={{ flex: 1, textAlign: 'center', minWidth: 80 }}>Allow Null</Box>
                                    <Box sx={{ flex: 2, minWidth: 0 }}>Default Value</Box>
                                    <Box sx={{ width: 50, textAlign: 'center' }}>Action</Box>
                                </Box>

                                <Box sx={{ maxHeight: 400, overflow: 'auto', backgroundColor: '#ffffff' }}>
                                    {newTableData.columns.map((column, index) => (
                                        <Box
                                            key={column.id}
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                alignItems: 'center',
                                                p: 2,
                                                borderBottom: index < newTableData.columns.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                backgroundColor: column.isPrimaryKey ? '#fef3f2' : 'white',
                                                transition: 'background-color 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: column.isPrimaryKey ? '#fef3f2' : '#f8fafc'
                                                }
                                            }}
                                        >
                                            <Box sx={{ flex: 2.5, minWidth: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    placeholder="Enter column name..."
                                                    size="small"
                                                    value={column.name}
                                                    onChange={(e) => updateColumn(column.id, 'name', e.target.value)}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'white'
                                                        }
                                                    }}
                                                />
                                            </Box>

                                            <Box sx={{ flex: 2.5, minWidth: 0 }}>
                                                <Autocomplete
                                                    size="small"
                                                    options={dataTypes}
                                                    value={column.dataType || null}
                                                    onChange={(event, newValue) => updateColumn(column.id, 'dataType', newValue || '')}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Select data type..."
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'white'
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                    freeSolo
                                                    disableClearable
                                                />
                                            </Box>

                                            <Box sx={{ flex: 1, textAlign: 'center', minWidth: 80 }}>
                                                <Tooltip title={column.isPrimaryKey ? "Primary Key" : "Set as Primary Key"}>
                                                    <Checkbox
                                                        checked={column.isPrimaryKey}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setNewTableData(prev => ({
                                                                    ...prev,
                                                                    columns: prev.columns.map(col => ({
                                                                        ...col,
                                                                        isPrimaryKey: col.id === column.id,
                                                                        allowNull: col.id === column.id ? false : col.allowNull
                                                                    }))
                                                                }));
                                                            } else {
                                                                setNewTableData(prev => ({
                                                                    ...prev,
                                                                    columns: prev.columns.map(col =>
                                                                        col.id === column.id
                                                                            ? { ...col, isPrimaryKey: false }
                                                                            : col
                                                                    )
                                                                }));
                                                            }
                                                        }}
                                                        size="small"
                                                        sx={{
                                                            color: column.isPrimaryKey ? '#042560' : 'default',
                                                            '&.Mui-checked': {
                                                                color: '#042560'
                                                            }
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Box>

                                            <Box sx={{ flex: 1, textAlign: 'center', minWidth: 80 }}>
                                                <Tooltip title={column.allowNull ? "Allows NULL values" : "NOT NULL constraint"}>
                                                    <Checkbox
                                                        checked={column.allowNull}
                                                        onChange={(e) => updateColumn(column.id, 'allowNull', e.target.checked)}
                                                        disabled={column.isPrimaryKey}
                                                        size="small"
                                                        sx={{
                                                            color: column.allowNull ? '#2e7d32' : '#f59e0b',
                                                            '&.Mui-checked': {
                                                                color: '#2e7d32'
                                                            },
                                                            '&.Mui-disabled': {
                                                                color: '#cbd5e1'
                                                            }
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Box>

                                            <Box sx={{ flex: 2, minWidth: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    placeholder={column.isPrimaryKey ? "Auto increment" : "Default value..."}
                                                    size="small"
                                                    value={column.defaultValue}
                                                    onChange={(e) => updateColumn(column.id, 'defaultValue', e.target.value)}
                                                    disabled={column.isPrimaryKey}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: column.isPrimaryKey ? '#f8fafc' : 'white'
                                                        }
                                                    }}
                                                />
                                            </Box>

                                            <Box sx={{ width: 50, textAlign: 'center' }}>
                                                <Tooltip title={newTableData.columns.length === 1 ? "Cannot remove the last column" : "Remove column"}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => removeColumn(column.id)}
                                                            disabled={newTableData.columns.length === 1}
                                                            sx={{
                                                                color: newTableData.columns.length === 1 ? '#cbd5e1' : '#d32f2f',
                                                                '&:hover': {
                                                                    backgroundColor: newTableData.columns.length === 1 ? 'transparent' : '#fef2f2',
                                                                    color: newTableData.columns.length === 1 ? '#cbd5e1' : '#dc2626'
                                                                },
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                        >
                                                            <Remove fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>

                                <Box sx={{
                                    p: 2,
                                    borderTop: '1px solid #e2e8f0',
                                    backgroundColor: '#ffffff',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<Add />}
                                        onClick={addNewColumn}
                                        sx={{
                                            borderColor: '#2e7d32',
                                            color: '#2e7d32',
                                            '&:hover': {
                                                backgroundColor: '#f0fdf4',
                                                borderColor: '#16a34a',
                                                color: '#16a34a'
                                            }
                                        }}
                                    >
                                        Add Column
                                    </Button>

                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Chip
                                            label={`${newTableData.columns.filter(col => col.isPrimaryKey).length} Primary Key`}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                borderColor: '#042560',
                                                color: '#042560',
                                                backgroundColor: '#f0f9ff'
                                            }}
                                        />
                                        <Chip
                                            label={`${newTableData.columns.filter(col => !col.allowNull).length} NOT NULL`}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                borderColor: '#f59e0b',
                                                color: '#f59e0b',
                                                backgroundColor: '#fff7ed'
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </DialogContent>

                    <DialogActions sx={{
                        p: 3,
                        borderTop: '1px solid #e2e8f0',
                        backgroundColor: '#f8fafc',
                        gap: 1
                    }}>
                        <Button
                            onClick={() => {
                                setShowNewTableDialog(false);
                                setNewTableData({
                                    tableName: '',
                                    columns: [
                                        {
                                            id: 1,
                                            name: '',
                                            dataType: '',
                                            isPrimaryKey: false,
                                            allowNull: true,
                                            defaultValue: ''
                                        }
                                    ]
                                });
                            }}
                            variant="outlined"
                            sx={{
                                borderColor: '#d1d5db',
                                color: '#6b7280',
                                '&:hover': {
                                    backgroundColor: '#f9fafb',
                                    borderColor: '#9ca3af'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateTable}
                            variant="contained"
                            startIcon={<CreateNewFolder />}
                            sx={{
                                backgroundColor: '#042560',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: '#031e4d'
                                },
                                '&:disabled': {
                                    backgroundColor: '#e5e7eb',
                                    color: '#9ca3af'
                                }
                            }}
                        >
                            Create Table
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showImportDialog}
                    onClose={(event, reason) => {
                        if (reason !== "backdropClick") {
                            setShowImportDialog(false);
                        }
                    }}
                    disableEscapeKeyDown
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                        }
                    }}
                >
                    <DialogTitle
                        sx={{
                            fontWeight: 600,
                            pb: 1,
                            fontSize: "1.1rem",
                            backgroundColor: '#059c09',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <CloudUpload sx={{ fontSize: 18 }} />
                        Import Data
                    </DialogTitle>

                    <DialogContent sx={{ p: 2 }}>
                        <Box sx={{ mt: 2 }}>
                            {!importFile ? (
                                <Box
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        const file = e.dataTransfer.files[0];
                                        if (!file) return;

                                        const allowed = [".csv", ".xlsx"];
                                        const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

                                        if (!allowed.includes(ext)) {
                                            handleToastOpen("error", "❌ Only Excel (.xlsx) or CSV (.csv) files are allowed.");
                                            setImportFile(null);
                                            return;
                                        }
                                        setImportFile(file);
                                    }}
                                    sx={{
                                        border: "2px dashed #059c09",
                                        borderRadius: 2,
                                        p: 2.5,
                                        textAlign: "center",
                                        backgroundColor: "#f8fdf8",
                                        cursor: "pointer",
                                        transition: 'all 0.2s ease',
                                        "&:hover": {
                                            borderColor: '#058908',
                                            backgroundColor: "#f0fdf0"
                                        }
                                    }}
                                >
                                    <CloudUpload sx={{ fontSize: 32, color: "#059c09", mb: 1 }} />
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                        Drop file here
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: "block", mb: 1.5, color: 'text.secondary' }}>
                                        OR
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        component="label"
                                        size="small"
                                        sx={{
                                            backgroundColor: '#042560',
                                            textTransform: "none",
                                            borderRadius: 1.5,
                                            px: 2,
                                            "&:hover": {
                                                backgroundColor: '#031e4d',
                                            }
                                        }}
                                    >
                                        Browse
                                        <input
                                            type="file"
                                            hidden
                                            accept=".csv,.xlsx"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;

                                                const allowed = [".csv", ".xlsx"];
                                                const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

                                                if (!allowed.includes(ext)) {
                                                    handleToastOpen("error", "Only Excel (.xlsx) or CSV (.csv) files are allowed.");
                                                    setImportFile(null);
                                                    return;
                                                }
                                                setImportFile(file);
                                            }}
                                        />
                                    </Button>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        p: 1.5,
                                        border: "1px solid #059c09",
                                        borderRadius: 2,
                                        backgroundColor: '#f8fdf8',
                                        mb: 1
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            fontWeight: 500
                                        }}
                                    >
                                        📄 {importFile.name}
                                    </Typography>
                                    <Button
                                        size="small"
                                        color="error"
                                        onClick={() => setImportFile(null)}
                                        sx={{
                                            minWidth: "auto",
                                            p: "2px 8px",
                                            fontSize: "0.7rem"
                                        }}
                                    >
                                        Remove
                                    </Button>
                                </Box>
                            )}

                            <Typography
                                variant="caption"
                                sx={{
                                    display: "block",
                                    color: "text.secondary",
                                    mb: 0.,
                                    mt: 1.5,
                                    textAlign: 'center'
                                }}
                            >
                                Excel (.xlsx) or CSV (.csv) only
                            </Typography>

                            <Typography variant="subtitle2" sx={{ mb: 1, fontSize: "0.85rem", fontWeight: 600 }}>
                                File Type
                            </Typography>
                            <RadioGroup
                                row
                                value={importOptions.withHeader ? "with" : "without"}
                                onChange={(e) =>
                                    setImportOptions((prev) => ({
                                        ...prev,
                                        withHeader: e.target.value === "with"
                                    }))
                                }
                                sx={{ gap: 1 }}
                            >
                                <FormControlLabel
                                    value="with"
                                    control={
                                        <Radio
                                            size="small"
                                            sx={{
                                                color: '#059c09',
                                                '&.Mui-checked': { color: '#059c09' }
                                            }}
                                        />
                                    }
                                    label="With Header"
                                    sx={{
                                        border: importOptions.withHeader ? '1px solid #059c09' : '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        px: 1,
                                        py: 0.5,
                                        m: 0,
                                        backgroundColor: importOptions.withHeader ? '#f8fdf8' : 'transparent',
                                        '& .MuiFormControlLabel-label': { fontSize: '0.8rem' }
                                    }}
                                />
                                <FormControlLabel
                                    value="without"
                                    control={
                                        <Radio
                                            size="small"
                                            sx={{
                                                color: '#059c09',
                                                '&.Mui-checked': { color: '#059c09' }
                                            }}
                                        />
                                    }
                                    label="No Header"
                                    sx={{
                                        border: !importOptions.withHeader ? '1px solid #059c09' : '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        px: 1,
                                        py: 0.5,
                                        m: 0,
                                        backgroundColor: !importOptions.withHeader ? '#f8fdf8' : 'transparent',
                                        '& .MuiFormControlLabel-label': { fontSize: '0.8rem' }
                                    }}
                                />
                            </RadioGroup>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
                        <Button
                            onClick={() => setShowImportDialog(false)}
                            variant="outlined"
                            size="small"
                            sx={{
                                textTransform: "none",
                                borderRadius: 1.5,
                                borderColor: '#e0e0e0',
                                color: 'text.secondary',
                                "&:hover": {
                                    borderColor: '#059c09',
                                    backgroundColor: '#f8fdf8'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleImportData}
                            variant="contained"
                            size="small"
                            disabled={!importFile}
                            startIcon={<CloudUpload />}
                            sx={{
                                backgroundColor: importFile ? '#042560' : '#e0e0e0',
                                textTransform: "none",
                                borderRadius: 1.5,
                                "&:hover": importFile ? {
                                    backgroundColor: '#031e4d',
                                } : {},
                                "&:disabled": {
                                    backgroundColor: '#f5f5f5',
                                    color: '#9e9e9e'
                                }
                            }}
                        >
                            Import
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showColumnDialog}
                    onClose={() => setShowColumnDialog(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        {columnDialogMode === 'add' ? 'Add New Column' :
                            columnDialogMode === 'edit' ? 'Edit Column' : 'Delete Column'}
                    </DialogTitle>
                    <DialogContent>
                        {columnDialogMode === 'delete' ? (
                            <Box sx={{ py: 2 }}>
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        Are you sure you want to delete the column "{columnDialogData.columnName}"?
                                        This action cannot be undone and will permanently remove all data in this column.
                                    </Typography>
                                </Alert>
                            </Box>
                        ) : (
                            <Grid container spacing={3} sx={{ mt: 1 }}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Column Name"
                                        value={columnDialogData.columnName || ''}
                                        onChange={(e) => setColumnDialogData(prev => ({
                                            ...prev,
                                            columnName: e.target.value
                                        }))}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        fullWidth
                                        value={columnDialogData.dataType || null}
                                        onChange={(event, newValue) => {
                                            setColumnDialogData(prev => ({
                                                ...prev,
                                                dataType: newValue
                                            }));
                                        }}
                                        options={dataTypes}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Data Type"
                                                variant="outlined"
                                            />
                                        )}
                                        freeSolo
                                        disableClearable={false}
                                    />

                                </Grid>
                                {columnDialogMode !== 'edit' && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Default Value"
                                                value={columnDialogData.defaultValue || ''}
                                                onChange={(e) => setColumnDialogData(prev => ({
                                                    ...prev,
                                                    defaultValue: e.target.value
                                                }))}
                                                helperText="Leave empty for no default value"
                                            />
                                        </Grid>
                                    </>
                                )}
                                {columnDialogMode === 'edit' && (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="New Column Name"
                                            value={columnDialogData.newName || ''}
                                            onChange={(e) => setColumnDialogData(prev => ({
                                                ...prev,
                                                newName: e.target.value
                                            }))}
                                            helperText="Leave same as current name if you don't want to rename"
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowColumnDialog(false)}>Cancel</Button>
                        <Button
                            onClick={() => {
                                if (columnDialogMode === 'add') {
                                    handleAddColumn();
                                } else if (columnDialogMode === 'edit') {
                                    handleModifyColumn();
                                } else {
                                    handleDeleteColumn();
                                }
                            }}
                            variant="contained"
                            color={columnDialogMode === 'delete' ? 'error' : 'primary'}
                            sx={columnDialogMode === 'delete' ? {} : {
                                backgroundColor: '#042560',
                                color: 'white',
                                '&:hover': { backgroundColor: '#031e4d' }
                            }}
                        >
                            {columnDialogMode === 'add' ? 'Add Column' :
                                columnDialogMode === 'edit' ? 'Update Column' : 'Delete Column'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showQueryDialog}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                            handleCancelQueryDialog();
                        }
                    }}
                    disableEscapeKeyDown
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        sx: { height: '90vh' }
                    }}
                >
                    <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Code sx={{ color: '#042560' }} />
                            <Typography variant="h6">Execute SQL Query</Typography>
                            <Chip
                                label={`Database: ${selectedDatabase}`}
                                size="small"
                                sx={{ backgroundColor: '#f0f9ff', color: '#042560' }}
                            />
                            {selectedTable && (
                                <Chip
                                    label={`Table: ${selectedTable}`}
                                    size="small"
                                    sx={{ backgroundColor: '#f0fdf4', color: '#3CB043' }}
                                />
                            )}
                        </Box>
                    </DialogTitle>

                    <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
                        <Grid container sx={{
                            height: '100%',
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Grid item xs={0} sm={5} md={4} sx={{
                                display: { xs: 'none', sm: 'block' },
                                borderRight: { sm: '1px solid #e2e8f0' },
                                height: '100%'
                            }}>
                                <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                                        SQL Templates
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                        Click any template to use it
                                    </Typography>
                                </Box>

                                <Tabs
                                    orientation="vertical"
                                    value={sqlTemplateTab}
                                    onChange={(e, newValue) => setSqlTemplateTab(newValue)}
                                    sx={{
                                        borderRight: 1,
                                        borderColor: 'divider',
                                        '& .MuiTab-root': {
                                            alignItems: 'flex-start',
                                            textAlign: 'left'
                                        }
                                    }}
                                >
                                    <Tab label="SELECT" sx={{ color: '#042560', '&.Mui-selected': { backgroundColor: '#f8fafc' } }} />
                                    <Tab label="INSERT" sx={{ color: '#042560', '&.Mui-selected': { backgroundColor: '#f8fafc' } }} />
                                    <Tab label="UPDATE" sx={{ color: '#f59e0b', '&.Mui-selected': { backgroundColor: '#f8fafc' } }} />
                                    {(selectedDatabase !== 'conas-prod-db' || !hasUserDelete) && (
                                        <Tab label="DELETE" sx={{ color: '#dc2626', '&.Mui-selected': { backgroundColor: '#f8fafc' } }} />
                                    )}
                                    <Tab
                                        label="CREATE"
                                        sx={{ color: '#16a34a', '&.Mui-selected': { backgroundColor: '#f8fafc' } }}
                                        value={hasUserDelete ? 4 : 3}
                                    />
                                    <Tab
                                        label="ALTER"
                                        sx={{ color: '#7c3aed', '&.Mui-selected': { backgroundColor: '#f8fafc' } }}
                                        value={hasUserDelete ? 5 : 4}
                                    />
                                </Tabs>

                                <Box sx={{
                                    p: 2,
                                    height: 'calc(100% - 200px)',
                                    overflow: 'auto'
                                }}>
                                    {getSqlTemplates().map((template, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                mb: 2,
                                                p: 2,
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    backgroundColor: '#f8fafc',
                                                    borderColor: '#3CB043'
                                                }
                                            }}
                                            onClick={() => setCustomQuery(template.query)}
                                        >
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
                                                {template.name}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.75rem',
                                                    color: '#6b7280',
                                                    backgroundColor: '#f8fafc',
                                                    p: 1,
                                                    borderRadius: '4px',
                                                    whiteSpace: 'pre-wrap',
                                                    maxHeight: '100px',
                                                    overflow: 'auto'
                                                }}
                                            >
                                                {template.query}
                                            </Typography>
                                        </Box>
                                    ))}

                                    {getSqlTemplates().length === 0 && (
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography variant="body2" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
                                                No templates available for this section
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>

                            <Grid item xs={12} sm={7} md={8} sx={{
                                height: { xs: '100%', sm: '100%' },
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: { xs: '400px', sm: 'auto' }
                            }}>
                                <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                                        Query Editor
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                        Write or modify your SQL query below
                                    </Typography>
                                </Box>

                                <Box sx={{ flex: 1, p: 2 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        value={customQuery}
                                        onChange={(e) => setCustomQuery(e.target.value)}
                                        placeholder="Enter your SQL query here or select a template from the left panel..."
                                        variant="outlined"
                                        sx={{
                                            height: '100%',
                                            '& .MuiOutlinedInput-root': {
                                                height: '100%',
                                                alignItems: 'flex-start',
                                                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                                fontSize: '0.9rem',
                                                lineHeight: 1.5,
                                                '& textarea': {
                                                    height: '100% !important',
                                                    overflow: 'auto !important'
                                                }
                                            }
                                        }}
                                    />
                                </Box>

                                <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 1 }}>
                                        💡 Tip: Use templates from the left panel or write custom queries.
                                        {!hasUserDelete && (
                                            <span style={{ color: '#dc2626' }}>
                                                {" "}DELETE operations require special permissions.
                                            </span>
                                        )}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#16a34a', display: 'block' }}>
                                        ✅ SELECT queries will replace current table data. Other queries will refresh the table.
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions sx={{ borderTop: '1px solid #e2e8f0', p: 2 }}>
                        <Button
                            onClick={handleCancelQueryDialog}
                            sx={{ color: '#6b7280' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleExecuteQuery}
                            variant="contained"
                            disabled={!customQuery.trim()}
                            startIcon={<PlayArrow />}
                            sx={{
                                backgroundColor: '#042560',
                                color: 'white',
                                '&:hover': { backgroundColor: '#031e4d' },
                                '&:disabled': {
                                    backgroundColor: '#e5e7eb',
                                    color: '#9ca3af'
                                }
                            }}
                        >
                            Execute Query
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={showExportDialog}
                    onClose={() => setShowExportDialog(false)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CloudDownload sx={{ color: COMMON_COLORS.success }} />
                        Export Data
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Choose the format for exporting your data:
                        </Typography>

                        <FormControl component="fieldset" fullWidth>
                            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                                Export Format
                            </FormLabel>
                            <RadioGroup
                                value={exportFormat}
                                onChange={(e) => setExportFormat(e.target.value)}
                            >
                                <FormControlLabel
                                    value="csv"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={500}>
                                                CSV (Comma Separated Values)
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Compatible with Excel, Google Sheets, and most applications
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    value="xlsx"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={500}>
                                                Excel (.xlsx)
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Native Excel format with better formatting support
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </RadioGroup>
                        </FormControl>

                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                {filteredTableData.length} rows will be exported with {visibleColumns.length} columns.
                            </Typography>
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setShowExportDialog(false)}
                            sx={{ color: COMMON_COLORS.gray }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmExport}
                            variant="contained"
                            startIcon={<CloudDownload />}
                            sx={{
                                backgroundColor: COMMON_COLORS.success,
                                color: 'white',
                                '&:hover': { backgroundColor: '#2e7d32' }
                            }}
                        >
                            Export {exportFormat.toUpperCase()}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showDeleteDialog}
                    onClose={() => setShowDeleteDialog(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning sx={{ color: '#f59e0b' }} />
                        Delete Options
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Choose how you want to delete data:
                        </Typography>
                        <RadioGroup
                            value={deleteType}
                            onChange={(e) => setDeleteType(e.target.value)}
                        >
                            <FormControlLabel
                                value="selected"
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="body2" fontWeight={500}>
                                            Delete Selected Rows ({selectedRows.length} rows)
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Only removes the rows you have selected
                                        </Typography>
                                    </Box>
                                }
                                disabled={selectedRows.length === 0}
                            />
                            {hasUserTruncate && (
                                <FormControlLabel
                                    value="truncate"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={500}>
                                                Truncate Table
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Fast deletion of all rows and resets auto-increment counters
                                            </Typography>
                                        </Box>
                                    }
                                />
                            )}
                            {hasUserDrop && (
                                <FormControlLabel
                                    value="drop"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={500}>
                                                Drop Table
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Completely removes the table and all its data
                                            </Typography>
                                        </Box>
                                    }
                                />
                            )}
                        </RadioGroup>

                        {deleteType === 'truncate' && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    This will delete ALL rows in the table and reset auto-increment counters.
                                </Typography>
                            </Alert>
                        )}

                        {deleteType === 'drop' && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    This will permanently delete the entire table structure and ALL data. This action cannot be undone.
                                </Typography>
                            </Alert>
                        )}

                        {deleteType === 'selected' && selectedRows.length > 0 && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    {selectedRows.length} row(s) will be permanently deleted.
                                </Typography>
                            </Alert>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowDeleteDialog(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                handleUnifiedDelete();
                                setShowDeleteDialog(false);
                            }}
                            variant="contained"
                            color="error"
                            disabled={deleteType === 'selected' && selectedRows.length === 0}
                        >
                            {deleteType === 'truncate' ? 'Truncate Table' :
                                deleteType === 'drop' ? 'Drop Table' :
                                    `Delete ${selectedRows.length} Row(s)`}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Menu
                    anchorEl={actionMenu.anchor}
                    open={Boolean(actionMenu.anchor)}
                    onClose={() => setActionMenu({ anchor: null, rowId: null })}
                >
                    <MenuItem onClick={() => {
                        handleEditRow(actionMenu.rowId);
                        setActionMenu({ anchor: null, rowId: null });
                    }}>
                        <Edit sx={{ mr: 1, fontSize: 18 }} /> Edit
                    </MenuItem>
                    <MenuItem onClick={() => {
                        const row = tableData.find(r => getRowIdentifier(r, tableColumns) === actionMenu.rowId); // Use dynamic identifier
                        navigator.clipboard.writeText(JSON.stringify(row, null, 2));
                        setActionMenu({ anchor: null, rowId: null });
                        showSnackbar('Row copied to clipboard', 'success');
                    }}>
                        <ContentCopy sx={{ mr: 1, fontSize: 18 }} /> Copy
                    </MenuItem>
                </Menu>

                <Menu
                    anchorEl={headerMenu.anchor}
                    open={Boolean(headerMenu.anchor)}
                    onClose={() => setHeaderMenu({ anchor: null, column: null })}
                >
                    <MenuItem onClick={() => {
                        setColumnDialogMode('edit');
                        setColumnDialogData({
                            originalName: headerMenu.column.name,
                            columnName: headerMenu.column.name,
                            newName: headerMenu.column.name,
                            newDataType: headerMenu.column.dataType,
                            nullable: headerMenu.column.nullable,
                            defaultValue: headerMenu.column.defaultValue
                        });
                        setShowColumnDialog(true);
                        setHeaderMenu({ anchor: null, column: null });
                    }}>
                        <Edit sx={{ mr: 1, fontSize: 18 }} /> Edit Column
                    </MenuItem>
                    {hasUserDelete && (
                        <>
                            <Divider />
                            <MenuItem
                                onClick={() => {
                                    setColumnDialogMode('delete');
                                    setColumnDialogData({
                                        columnName: headerMenu.column.name
                                    });
                                    setShowColumnDialog(true);
                                    setHeaderMenu({ anchor: null, column: null });
                                }}
                                sx={{ color: COMMON_COLORS.danger }}
                            >
                                <Delete sx={{ mr: 1, fontSize: 18 }} /> Delete Column
                            </MenuItem>
                        </>
                    )}
                </Menu>
            </Container>
        </Box>
    );
};

export default DataManagementView;

